import { cloneDeep } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { imageKit } from '@meitu/wasm-lib';
import { BaseOperateView, getFramework, useIdentity, useScale } from '@/framework';
import type { IPoint } from '@/version-types';
import type { IntelligentColorPlugin } from '../controller';
import { getIntelligentColorState, useIntelligentColorState } from '../states/intelligent-color-state';
import { SMART_MASK_PEN_EVENTS } from '../types';
import daubPenCursor from './assets/daub-pen.svg';
import './smart-mask-pen.less';

export const SmartMaskPen = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const scale = useScale();
    const isProtectPen = useIntelligentColorState()((state) => state.isProtectPen);
    const penSize = useIntelligentColorState()((state) => state.intelligentPenSize);
    const setIntelligentPreData = useIntelligentColorState()((state) => state.setIntelligentPreData);

    const intelligentController =
        framework.service.registration.getInstance<IntelligentColorPlugin>('IntelligentColorPlugin');
    // 画笔光标大小。底层绘制参数使用半径并按缩放换算，屏幕上的直径应与 penSize 保持一致。
    const penCursorSize = penSize;

    const [mousePosition, setMousePosition] = useState<IPoint>({ x: -10000, y: 0 });
    const [inStage, setInStage] = useState<boolean>(false);
    const isInMaskPenRef = useRef<boolean>(false);
    const viewportPoint = inStage ? framework.service.transform.getViewportPoint(mousePosition) : mousePosition;

    const webglCanvasRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (webglCanvasRef.current) {
            webglCanvasRef.current.appendChild(imageKit.getGLCanvas()!);
        }
    }, []);

    useEffect(() => {
        if (!isProtectPen) return;

        intelligentController.setSmearPenSize(penSize / 2 / scale);
    }, [intelligentController, isProtectPen, penSize, scale]);

    useEffect(() => {
        const checkInRect = (point: IPoint) => {
            const stageIndex = framework.service.frame.getActiveIndex();
            return framework.service.transform.checkPointInFrame(point, stageIndex);
        };

        const moveHandle = (e) => {
            const point = { x: e.clientX, y: e.clientY };
            const position = point;
            const inRect = checkInRect(point);
            setMousePosition(position);
            setInStage(inRect);
            if (inRect) {
                if (!isInMaskPenRef.current) return;
                intelligentController.updateLayer();
            }
        };

        const downHandle = (e) => {
            const point = { x: e.clientX, y: e.clientY };
            isInMaskPenRef.current = checkInRect(point);
        };

        const upHandle = async () => {
            if (!isInMaskPenRef.current) return;

            framework.service.mask.show({ loading: true });

            try {
                const old = cloneDeep(getIntelligentColorState(identity).intelligentPreData);
                const maskCode = await intelligentController.getMaskData();

                if (maskCode) {
                    old.maskCode = maskCode;
                }

                const selectedData = getIntelligentColorState(identity).selectedInfo;
                const { color } = selectedData;
                const isSelectAll = !color;

                if (isSelectAll) {
                    await intelligentController.renderAllIntelligentPreview();
                } else {
                    await intelligentController.renderIntelligentPreview(color);
                }

                intelligentController.updateLayer();
                setIntelligentPreData(cloneDeep(old));

                const smartMaskType = getIntelligentColorState(identity).smartPenType;
                framework.service.events.emit(SMART_MASK_PEN_EVENTS.drawEnd, smartMaskType);

                console.log('===>updatePreviewList');
            } catch (error) {
                console.error('error:', error);
            } finally {
                isInMaskPenRef.current = false;
                framework.service.mask.hide();
            }
        };

        document.addEventListener('mouseup', upHandle);
        document.addEventListener('mousedown', downHandle);
        document.addEventListener('mousemove', moveHandle);
        return () => {
            document.removeEventListener('mouseup', upHandle);
            document.removeEventListener('mousedown', downHandle);
            document.removeEventListener('mousemove', moveHandle);
        };
    }, [
        framework.service.events,
        framework.service.frame,
        framework.service.mask,
        framework.service.transform,
        identity,
        intelligentController,
        setIntelligentPreData,
    ]);

    return (
        <div className="m-smart-mask-pen">
            {/* 把 gl 画布挂载到 dom 上，接收 mousemove 事件 */}
            <BaseOperateView layer={framework.service.frame.getMainLayer(framework.service.frame.getActiveFrameId())!}>
                <div className="m-smart-mask-pen-webgl" ref={webglCanvasRef}></div>
            </BaseOperateView>

            {inStage && isProtectPen ? (
                <div
                    style={{
                        position: 'absolute',
                        width: penCursorSize,
                        height: penCursorSize,
                        left: 0,
                        top: 0,
                        transform: `translate(${viewportPoint.x}px, ${viewportPoint.y}px) translate(-50%, -50%)`,
                    }}
                    className="m-draw-pen"
                >
                    <img src={daubPenCursor} style={{ display: 'block' }} height="100%" width="100%" alt="" />
                </div>
            ) : null}
        </div>
    );
});
